import {
  BIDS_FORM_FIELD_NAME,
  JORNAYA_TCPA_FORM_FIELD_NAME,
  JORNAYA_TOKEN_FORM_FIELD_NAME,
  LS_SUBMISSION_KEY,
} from "@lgp/shared/constants";
import useTrackGAEvents from "@lgp/shared/react/hooks/useTrackGAEvents";
import useTriggerPostMessage from "@lgp/shared/react/hooks/useTriggerPostMessage";
import { hasOwnProp, isEmpty } from "@lgp/shared/utils";
import { toSnake } from "@lgp/shared/utils/string";
import { createContext, useCallback, useEffect, useState } from "react";
import createProjectIcon from "./createProjectIcon";

const { NODE_ENV } = process.env;

const initialPreReadyState = Object.freeze({
  configData: null,
  isLoading: true,
});

const initialAppState = Object.freeze({
  isLoaded: false,
  isDisabled: false,
  jornaya: null,
  maxSectionCompleted: null,
  currentSection: 0,
  prevCurrentSection: 0,
  hasSearchSection: false,
});

const initialFormState = Object.freeze({
  isLoaded: false,
  isSubmitted: false,
  isSubmitting: false,
  isDisabled: false,
  isConversion: false,
  conversionId: null,
  error: null,
  data: null,
});

const initialContext = {
  appState: initialAppState,
  formState: initialFormState,
  setFormState: () => {},
  triggerPostMessage: async () => {},
  trackGAEvent: () => {},
  setPrevSection: () => {},
  setNextSection: () => {},
  setSection: () => {},
  setFormDisabled: () => {},
};

const whiteListedUrlParams = {
  FBTestEventID: "FBTestEventID",
  Match_With_Partner_ID: "Match_With_Partner_ID",
  Name: "Name",
  FirstName: "FirstName",
  LastName: "LastName",
  Address: "Address",
  City: "City",
  State: "State",
  Zip: "Zip",
  Email: "Email",
  Primary_Phone: "Primary_Phone",
  name: "Name",
  fname: "FirstName",
  lname: "LastName",
  address: "Address",
  city: "City",
  state: "State",
  zip: "Zip",
  email: "Email",
  phone: "Primary_Phone",
  Test_Lead: "Test_Lead",
  src: "SRC",
  SRC: "SRC",
};

export const appContext = createContext(initialContext);

const { Provider } = appContext;

const AppProvider = (props) => {
  const { parentHost, parentUrl, formConfig, children } = props;
  const [preReadyState, setPreReadyState] = useState({
    ...initialPreReadyState,
    parentHost,
    parentUrl,
    formConfig,
  });
  const [appState, setAppState] = useState(initialAppState);
  const [formState, setFormState] = useState(initialFormState);

  const { configData } = preReadyState;
  const { isDisabled } = appState;

  // These values must be set before starting the app.
  const error = configData === false;
  const isLoading = !error && configData === null;

  // Callback to check the form is disabled.
  const checkFormDisabled = useCallback((project) => {
    const key = `${LS_SUBMISSION_KEY}_${toSnake(project)}`;
    let isDisabled = false;

    try {
      const exp = localStorage.getItem(key);
      if (exp) {
        isDisabled = Date.now() < Number(exp);
        if (!isDisabled) {
          localStorage.removeItem(key);
        }
      }
    } catch (err) {
      console.log(err);
    }

    return isDisabled;
  }, []);

  // Callback to mark the form as disabled.
  const setFormDisabled = useCallback((project, timeout) => {
    const key = `${LS_SUBMISSION_KEY}_${toSnake(project)}`;
    try {
      localStorage.setItem(key, Date.now() + timeout * 1000);
    } catch (err) {
      console.log(err);
    }
  }, []);

  // Send / Receive messages from parent window.
  const triggerPostMessage = useTriggerPostMessage(parentHost);

  // Tracks GA event.
  const trackGAEvent = useTrackGAEvents(
    parentHost,
    formConfig,
    formState?.data
  );

  // Sets the current section section.
  const setSection = useCallback(
    function setSection(newCurrentSection, newMaxSectionCompleted) {
      const { currentSection, sections } = appState;

      if (newCurrentSection < 0) {
        newCurrentSection = 0;
      } else if (newCurrentSection >= sections.length) {
        newCurrentSection = sections.length;
      }

      setAppState({
        ...appState,
        maxSectionCompleted: newMaxSectionCompleted,
        currentSection: newCurrentSection,
        prevCurrentSection: currentSection,
      });
    },
    [appState]
  );

  // Sets the prev section.
  const setPrevSection = useCallback(
    function setNextSection() {
      const { currentSection, maxSectionCompleted } = appState;
      if (currentSection > 0) {
        let newCurrentSection = currentSection - 1;
        const section = appState.sections[newCurrentSection];
        if (!section.fields) {
          newCurrentSection = newCurrentSection - 1;
        }
        setSection(newCurrentSection, maxSectionCompleted);
      }
    },
    [appState, setSection]
  );

  // Sets the next section.
  const setNextSection = useCallback(
    function setNextSection() {
      const { sections, currentSection, maxSectionCompleted } = appState;

      trackGAEvent("lead_form_section_completed", {
        lead_form_section_id: `section_${currentSection + 1}`,
      });

      let newMaxSectionCompleted = currentSection;
      if (newMaxSectionCompleted < maxSectionCompleted) {
        newMaxSectionCompleted = maxSectionCompleted;
      }

      let newCurrentSection = currentSection;
      if (currentSection < sections.length - 1) {
        newCurrentSection = currentSection + 1;
      }

      setSection(newCurrentSection, newMaxSectionCompleted);
    },
    [appState, setSection, trackGAEvent]
  );

  // Handles listensing the messages from the parent window.
  const handlePostMessage = useCallback(
    function handlePostMessage(e) {
      if (e.origin !== parentHost) {
        return;
      }

      const message = e.data;
      const { action, payload } = message;
      switch (action) {
        case "lgp:ready":
          setPreReadyState((prevState) => ({
            ...prevState,
            ...payload,
          }));
          break;
        case "lgp:jornaya-ready":
          setAppState((prevState) => ({
            ...prevState,
            ...payload,
          }));
          break;

        default:
          break;
      }
    },
    [parentHost]
  );

  useEffect(() => {
    window.addEventListener("message", handlePostMessage);

    return () => {
      window.removeEventListener("message", handlePostMessage);
    };
  }, [handlePostMessage]);

  useEffect(
    function setInitialStatesEffect() {
      const { parentUrl, parentHost, configData } = preReadyState;
      let hasSearchSection = false;

      if (isLoading) {
        window.top.postMessage(
          {
            action: "lgp:ready",
          },
          parentHost
        );

        return;
      }

      const formOptions = Object.assign(
        {},
        {
          showProgressBar: true,
          formWidth: 80,
          startButtonWidth: 60,
          submitButtonColor: "",
          submitButtonFontColor: "#FFFFFF",
        },
        configData.formOptions
      );

      let landingPageUrl = parentUrl;
      const urlParams = landingPageUrl.searchParams;

      // Set the initial form state.
      const newData = {
        ...configData.boberdooFields,
        Lead_ID: "",
        [BIDS_FORM_FIELD_NAME]: [],
        FBBrowserID: "",
        FBClickID: "",
        FBPixelID: "",
        FBTestEventID: "",
        Google_Click_ID: "",
        IPQS: "",
        [JORNAYA_TOKEN_FORM_FIELD_NAME]: "",
        Landing_Page: landingPageUrl.href.split("?")[0],
        LeadiD_Token: "",
        Trusted_Form_URL: "",
        TCPA_Consent: "Yes",
        User_Agent: window.navigator.userAgent,
        TCPA_Language: `By tapping "${
          formOptions.submitButtonLabel ?? "Get Prices Now"
        }", you authorize HelloProject and up to three (3) home service partners that can help with your project to call or text you on the phone number provided using autodialed and prerecorded calls or messages even if your number is on any federal, state, or local do not call list. Your consent to this agreement is not required to purchase products or services. By using this website, you agree to HelloProject <a href="https://www.helloprojectusa.com/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>, <a href="https://www.helloprojectusa.com/consumer-tos/" target="_blank" rel="noreferrer">Terms and Conditions</a> &amp; <a href="https://www.helloprojectusa.com/privacy-policy/#ccpa" target="_blank" rel="noreferrer">CCPA</a> policy. Msg and data rates may apply. Reply HELP for help or STOP for cancel. We respect your privacy.</span>`,
      };

      // Set the form state properties from landing page url's query string.
      configData.urlFields = configData.urlFields || {};
      let ttRedirectUrl =
        configData.ttRedirectUrl || "https://home.helloprojectusa.com/pros-tt";
      if (urlParams) {
        const entries = urlParams.entries();
        for (const [key, value] of entries) {
          if (configData.urlFields[key]) {
            newData[configData.urlFields[key]] = value;
          } else if (whiteListedUrlParams[key]) {
            newData[whiteListedUrlParams[key]] = value;
          } else if (key === "ttRedirectUrl") {
            ttRedirectUrl = value;
          }
        }
      }

      ttRedirectUrl = null;

      // Is it a test lead?
      if (
        (typeof newData.Test_Lead === "undefined" &&
          NODE_ENV === "development") ||
        newData.Test_Lead === "1" ||
        newData.Test_Lead === 1
      ) {
        newData.Test_Lead = 1;
      }

      // Override the Name field if the FirstName and Address have been pre-populated.
      if (newData.FirstName) {
        newData.Name = newData.FirstName;
        if (newData.LastName) {
          newData.Name = `${newData.FirstName} ${newData.LastName}`;
        }
      }

      // Set the empty name fields.
      if (hasOwnProp(newData, "Name")) {
        newData.FirstName = "";
        newData.LastName = "";
      }

      // Override the Address field if the Address fields have been pre-populated.
      if (newData.Address && newData.City && newData.State && newData.Zip) {
        newData.Address = `${newData.Address}, ${newData.City}, ${newData.State}, ${newData.Zip}`;
      } else {
        newData.Address = "";
        newData.City = "";
        newData.State = "";
      }

      configData.sections.forEach((section) => {
        if (!section.fields) {
          section.fields = [];
        }

        if (section.steps && section.steps.length > 0) {
          hasSearchSection = true;
        }

        section.fields.forEach((field) => {
          // Override the field label.
          if (
            field.type === "address" &&
            !isEmpty(field["lf-label"]) &&
            field["lf-label"].toLowerCase().indexOf("address") > -1
          ) {
            field["lf-label"] = "Home Location";
          }

          // Set defaults.
          if (field.type === "checkbox buttons") {
            newData[field.name] = [];
          } else if (isEmpty(newData[field.name])) {
            newData[field.name] = field.value ?? "";
          }

          // Set options icons.
          if (field["lf-options"]) {
            field["lf-options"].forEach((option) => {
              if (option["lf-icon"]) {
                option["lf-icon"] = createProjectIcon({
                  icon: option["lf-icon"],
                });
              }
            });
          }

          // Override tel mask.
          if (field.mask === "(999) 999-9999") {
            field.mask = "+1 (999) 999-9999";
          }
        });

        // Add the Jornaya hidden fields.
        section.fields.push({
          tag: "input",
          type: "hidden",
          id: JORNAYA_TCPA_FORM_FIELD_NAME,
          name: JORNAYA_TCPA_FORM_FIELD_NAME,
          "lf-label": newData.TCPA_Language,
        });

        section.fields.push({
          type: "jornaya",
          id: JORNAYA_TOKEN_FORM_FIELD_NAME,
          name: JORNAYA_TOKEN_FORM_FIELD_NAME,
        });
      });

      // Set the new config state.
      if (configData.sections.length < 2) {
        formOptions.showProgressBar = false;
      }

      // Check if disabled.
      const isDisabled = checkFormDisabled(newData.Project);

      setAppState((prevState) => ({
        ...prevState,
        ...configData,
        ...preReadyState,
        formOptions,
        matchEndpoint: "/leads/homeImprovement/match",
        ttRedirectUrl,
        hasSearchSection,
        isLoaded: true,
        isDisabled,
      }));

      // Set the form state.
      setFormState((prevState) => ({
        ...prevState,
        data: newData,
        isLoaded: true,
      }));
    },
    [isLoading, preReadyState, checkFormDisabled]
  );

  useEffect(
    function trackGALeadFormLoadedEventEffect() {
      if (!isDisabled && formState.isLoaded) {
        trackGAEvent("lead_form_loaded");
      }
    },
    [isDisabled, formState, trackGAEvent]
  );

  if (error) {
    console.log("Error loading the app.");
    return null;
  }

  if (isDisabled) {
    return (
      <div className="position-absolute w-100 h-100 d-flex flex-column align-items-center bg-white justify-content-center">
        <div className="m-3">
          <h2 className="text-muted">
            We received your request for a free quote and will be in contact
            with you within 24 hours. Thank you!
          </h2>
        </div>
      </div>
    );
  }

  if (isLoading || !appState.isLoaded || !formState.isLoaded) {
    return null;
  }

  return (
    <Provider
      value={{
        appState,
        formState,
        setFormState,
        triggerPostMessage,
        trackGAEvent,
        setPrevSection,
        setNextSection,
        setSection,
        setFormDisabled,
      }}
    >
      {children}
    </Provider>
  );
};

export default AppProvider;
